import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PRODUCT_PLACEHOLDER_SRC } from "../../constants/ProductConstants";
import styled from "styled-components";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { colors, styles } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import {
  PRODUCT_DETAIL_LARGE_IMAGE,
  PRODUCT_DETAIL_SMALL_IMAGE,
} from "../../constants/ImageConstants";
import LazyImage from "../image/LazyImage";

SwiperCore.use([Navigation]);

const ProductImageGallery = ({ product, isFeatured }) => {
  const INITIAL_SLIDE = 0;
  const { t } = useTranslation();
  const [swiper, setSwiper] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(INITIAL_SLIDE);

  useEffect(() => {
    if (!!swiper) slideTo(INITIAL_SLIDE);
  }, [product]);

  const slideTo = (index) => {
    swiper.slideToLoop(index);
    setCurrentSlide(index);
  };

  return (
    <Fragment>
      <div
        className="product-large-image-wrapper"
        style={{ position: "relative" }}
      >
        {(product.discount || product.new) && (
          <div className="product-img-badges">
            {!!product.tag && <span className="tag">{t(product.tag)}</span>}
            {!!product.discount && (
              <span className="pink">-{product.discount}%</span>
            )}
          </div>
        )}
        {!!product.image ? (
          <div>
            <SliderWrapper
              loop={true}
              slidesPerView={1}
              spaceBetween={12}
              navigation={product.image.length > 1}
              onSwiper={setSwiper}
              onSlideChange={(e) => {
                if (product.image.length > 1) setCurrentSlide(e.realIndex);
              }}
              effect="fade"
              className={"pt-0"}
            >
              {product.image &&
                product.image.map((single, key) => {
                  return (
                    <SwiperSlide key={key}>
                      <ItemWrapper>
                        <Image src={single} alt={product.name} />
                        {isFeatured && (
                          <FeaturedTag>
                            <span>{t("featured_tag")}</span>
                          </FeaturedTag>
                        )}
                      </ItemWrapper>
                    </SwiperSlide>
                  );
                })}
            </SliderWrapper>
            {product.image.length > 1 && (
              <Swiper
                slidesPerView={4}
                spaceBetween={15}
                breakpoints={{
                  768: {
                    slidesPerView: 2,
                  },
                  992: {
                    slidesPerView: 5,
                  },
                }}
              >
                {product.image.map((item, index) => {
                  return (
                    <SwiperSlide
                      key={index}
                      onClick={() => {
                        slideTo(index);
                      }}
                    >
                      <SmallItemWrapper active={currentSlide === index}>
                        <Image
                          src={item}
                          alt={product.name}
                          className={"small"}
                        />
                      </SmallItemWrapper>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
          </div>
        ) : (
          <div className="single-image">
            <img
              src={PRODUCT_PLACEHOLDER_SRC}
              className="img-fluid"
              alt={product.name}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

const FeaturedTag = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  background-color: ${colors.featuredTag};
  padding: 0 8px;
  color: #fff;
  border-radius: 3px;

  @media ${device.lg} {
    top: -10px;
    left: 20%;
  }
`;

const SmallItemWrapper = styled.div`
  border: 2px solid ${({ active }) => (active ? colors.primary : "#0000001f")};
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: 0.2s all ease;
  padding: 2px;

  &:hover {
    border-color: ${colors.primary};
  }

  @media ${device.lg} {
    margin-bottom: 1rem;
  }
`;

const Image = styled(LazyImage)`
  max-height: 30vh;
  border-radius: ${styles.card_border_radius};
  min-height: ${PRODUCT_DETAIL_LARGE_IMAGE.sm.height + "px"};
  width: 100%;
  @media ${device.maxSm} {
    width: -webkit-fill-available;
  }
  @media ${device.md} {
    max-height: none;
    min-height: ${PRODUCT_DETAIL_LARGE_IMAGE.md.height + "px"};
  }
  @media ${device.lg} {
    min-height: ${PRODUCT_DETAIL_LARGE_IMAGE.lg.height + "px"};
  }
  @media ${device.xl} {
    min-height: ${PRODUCT_DETAIL_LARGE_IMAGE.xl.height + "px"};
  }

  &.small {
    min-height: ${PRODUCT_DETAIL_SMALL_IMAGE.sm.height + "px"};
    @media ${device.md} {
      min-height: ${PRODUCT_DETAIL_SMALL_IMAGE.md.height + "px"};
    }
    @media ${device.lg} {
      min-height: ${PRODUCT_DETAIL_SMALL_IMAGE.lg.height + "px"};
    }
    @media ${device.xl} {
      min-height: ${PRODUCT_DETAIL_SMALL_IMAGE.xl.height + "px"};
    }
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0.5rem;
  border-radius: ${styles.card_border_radius};
`;

const SliderWrapper = styled(Swiper)`
  @media ${device.lg} {
    padding-top: 1rem !important;
  }

  .swiper-pagination {
    margin-top: 2rem;
  }

  .swiper-pagination-bullets {
    bottom: -2px;
  }

  .swiper-pagination-bullet-active {
    background: ${colors.primary};
  }

  .swiper-button-prev,
  .swiper-button-next {
    color: ${colors.primary};

    &:hover {
      &:after {
        box-shadow: 0 0 6px #0000004d;
      }
    }
  }

  .swiper-button-prev:after,
  .swiper-button-next:after {
    font-size: 22px;
    padding: 8px 14px;
    border-radius: 50%;
    box-shadow: 0 0 6px #0000002e;
    transition: 0.2s all ease;
    background: white;
  }
`;

ProductImageGallery.propTypes = {
  product: PropTypes.object,
};

export default ProductImageGallery;
